import { MouseEvent as ReactMouseEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/** Logical page keys used throughout the new-look prototype. */
export type View =
  | 'home'
  | 'services'
  | 'portfolio'
  | 'about'
  | 'contact'
  | 'tablefinch'
  | 'nyasa'
  | 'compasso'
  | 'privacy';

/** Maps a logical view to its real route path. */
export const VIEW_PATHS: Record<View, string> = {
  home: '/',
  services: '/services',
  portfolio: '/portfolio',
  about: '/about',
  contact: '/contact',
  tablefinch: '/table-finch',
  nyasa: '/nyasa',
  compasso: '/compasso',
  privacy: '/privacy',
};

/** The view whose route is exactly `path`, if any (for data-driven links). */
export const viewForPath = (path: string): View | undefined =>
  (Object.keys(VIEW_PATHS) as View[]).find((v) => VIEW_PATHS[v] === path);

export type GoFn = (view: View) => void;

/**
 * Returns a `go(view)` navigator that mirrors the prototype's API but drives
 * real React Router URLs, scrolling to top on each navigation.
 */
export const useGo = (): GoFn => {
  const navigate = useNavigate();
  return useCallback(
    (view: View) => {
      navigate(VIEW_PATHS[view] ?? '/');
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [navigate],
  );
};

export interface NavLink {
  href: string;
  onClick: (e: ReactMouseEvent) => void;
}

/**
 * Builds an anchor onClick that does client-side navigation but lets
 * modifier/middle clicks fall through to the browser, so cmd/ctrl-click still
 * opens a new tab — standard SPA-link behaviour. Safe to call inside a `.map`
 * (it is a plain factory, not a hook).
 */
export const navHandler =
  (go: GoFn, view: View) =>
  (e: ReactMouseEvent): void => {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    go(view);
  };

/**
 * Props for a single crawlable in-app link: a real `href` plus a navigating
 * `onClick`. For lists, prefer `navHandler(go, view)` with an explicit href.
 */
export const useNavLink = (view: View): NavLink => {
  const go = useGo();
  return { href: VIEW_PATHS[view], onClick: navHandler(go, view) };
};

/**
 * onClick for an in-app link to any path (e.g. with a query string, which
 * `navHandler` can't express). Modifier/middle clicks fall through to the browser.
 */
export const usePathLink = (path: string) => {
  const navigate = useNavigate();
  return (e: ReactMouseEvent): void => {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };
};

/** Contact form preselected with a product enquiry (see ContactPage PRODUCT_INTERESTS). */
export const contactFor = (interest: 'nyasa' | 'compasso' | 'tablefinch'): string =>
  `/contact?interest=${interest}`;
